import type { ReportRow } from '@/api/keywordMetrics';
import {
  campaignAdProductToReportId,
  keywordReportLookupKey,
  targetReportLookupKey,
} from '@/api/reportAdProducts';
import type { CampaignAdProduct } from '@/types/spCampaigns';
import type { CampaignNode, EntityGraph } from '@/utils/entityCrossFilter';
import { portfolioIdForCampaign } from '@/utils/entityCrossFilter/buildEntityGraph';
import type { KeywordAdProduct, UnifiedKeywordRow } from '@/api/unifiedKeywords';

/** Minimal keyword-page fields needed to join previous-period report rows. */
export type KeywordCompareRow = {
  keywordId: string;
  keywordText: string;
  matchType: string;
  campaignId: string;
  adGroupId?: string;
  adProduct: string;
  rowKind?: 'keyword' | 'auto_target';
};

/** Matches backend `keyword_natural_key`: `{campaign}|{text.lower}|{match.upper}`. */
export function keywordNaturalLookupKey(
  campaignId: unknown,
  keywordText: unknown,
  matchType: unknown
): string {
  const campaign = String(campaignId ?? '').trim();
  const text = String(keywordText ?? '').trim().toLowerCase();
  const match = String(matchType ?? '').trim().toUpperCase();
  if (!campaign || !text) return '';
  return `${campaign}|${text}|${match}`;
}

export const REPORT_AD_PRODUCT_IDS = [
  'sponsored_products',
  'sponsored_brands',
  'sponsored_display',
] as const;

export const AD_PRODUCT_METRIC_ROWS: Array<{
  reportId: (typeof REPORT_AD_PRODUCT_IDS)[number];
  label: CampaignAdProduct;
}> = [
  { reportId: 'sponsored_products', label: 'SP' },
  { reportId: 'sponsored_brands', label: 'SB' },
  { reportId: 'sponsored_display', label: 'SD' },
];

export type AdProductSummaryMetrics = Partial<DerivedMetricTotals>;

export type AdProductSummaryPayload = {
  buckets?: Array<{
    id?: string;
    label?: string;
    current?: AdProductSummaryMetrics | null;
    previous?: AdProductSummaryMetrics | null;
  }>;
};

/** `null` is unscoped. An empty set means the filter matched nothing. */
export function adProductCampaignKeysParam(
  keys: Set<string> | null | undefined
): string | undefined {
  if (keys == null) return undefined;
  if (keys.size === 0) return '-';
  return [...keys].sort().join(',');
}

function coerceAdProductMetrics(value: AdProductSummaryMetrics | null | undefined): DerivedMetricTotals {
  const raw: RawMetricTotals = {
    impressions: num(value?.impressions),
    clicks: num(value?.clicks),
    cost: num(value?.cost),
    purchases30d: num(value?.purchases30d),
    sales30d: num(value?.sales30d),
  };
  const derived = deriveMetrics(raw);
  const pick = (key: keyof DerivedMetricTotals) =>
    value?.[key] == null ? derived[key] : num(value[key]);
  return {
    ...derived,
    ctr: pick('ctr'),
    cpc: pick('cpc'),
    cr: pick('cr'),
    acos: pick('acos'),
    roas: pick('roas'),
    asp: pick('asp'),
    cpo: pick('cpo'),
  };
}

export function emptyAdProductMap(): Map<string, DerivedMetricTotals> {
  return new Map(
    AD_PRODUCT_METRIC_ROWS.map(({ reportId }) => [reportId, { ...EMPTY_DERIVED_METRICS }])
  );
}

/** Map the ad-product summary payload into the maps the workspace table already reads. */
export function adProductMapsFromSummary(data: AdProductSummaryPayload | null | undefined): {
  current: Map<string, DerivedMetricTotals>;
  previous: Map<string, DerivedMetricTotals>;
} {
  const current = emptyAdProductMap();
  const previous = emptyAdProductMap();
  for (const bucket of data?.buckets ?? []) {
    const id = String(bucket.id ?? '');
    if (!REPORT_AD_PRODUCT_IDS.includes(id as (typeof REPORT_AD_PRODUCT_IDS)[number])) continue;
    current.set(id, coerceAdProductMetrics(bucket.current));
    previous.set(id, coerceAdProductMetrics(bucket.previous));
  }
  return { current, previous };
}

export type RawMetricTotals = {
  impressions: number;
  clicks: number;
  cost: number;
  purchases30d: number;
  sales30d: number;
};

export type DerivedMetricTotals = RawMetricTotals & {
  ctr: number;
  cpc: number;
  cr: number;
  acos: number;
  roas: number;
  asp: number;
  cpo: number;
};

/** Account-level retail (non-ad) metrics — null until a Vendor Central account is linked. */
export type WorkspaceRetailKpiMetrics = {
  totalSales: number | null;
  organicSales: number | null;
  totalOrders: number | null;
  organicOrders: number | null;
  tacos: number | null;
  glanceViews?: number | null;
};

export const EMPTY_RAW_METRICS: RawMetricTotals = {
  impressions: 0,
  clicks: 0,
  cost: 0,
  purchases30d: 0,
  sales30d: 0,
};

export const EMPTY_DERIVED_METRICS: DerivedMetricTotals = {
  ...EMPTY_RAW_METRICS,
  ctr: 0,
  cpc: 0,
  cr: 0,
  acos: 0,
  roas: 0,
  asp: 0,
  cpo: 0,
};

export const EMPTY_RETAIL_KPI_METRICS: WorkspaceRetailKpiMetrics = {
  totalSales: null,
  organicSales: null,
  totalOrders: null,
  organicOrders: null,
  tacos: null,
  glanceViews: null,
};

export type WorkspaceMetricsMaps = {
  byAdProduct: Map<string, DerivedMetricTotals>;
  byCampaignKey: Map<string, DerivedMetricTotals>;
  byPortfolioId: Map<string, DerivedMetricTotals>;
  byAdGroupId: Map<string, DerivedMetricTotals>;
};

export type MetricPolarity = 'higher_is_better' | 'lower_is_better';

export type WorkspaceMetricColumnDef = {
  key: string;
  label: string;
  polarity: MetricPolarity;
  format: 'int' | 'currency' | 'percent' | 'ratio';
};

/** Column order: Spend, CPC, CTR, CR%, Orders, Impressions, Clicks, Sales, ACoS, ROAS, ASP */
export const WORKSPACE_METRIC_COLUMNS: WorkspaceMetricColumnDef[] = [
  { key: 'cost', label: 'Spend', polarity: 'lower_is_better', format: 'currency' },
  { key: 'cpc', label: 'CPC', polarity: 'lower_is_better', format: 'currency' },
  { key: 'ctr', label: 'CTR', polarity: 'higher_is_better', format: 'percent' },
  { key: 'cr', label: 'CR%', polarity: 'higher_is_better', format: 'percent' },
  { key: 'purchases30d', label: 'Orders', polarity: 'higher_is_better', format: 'int' },
  { key: 'impressions', label: 'Impressions', polarity: 'higher_is_better', format: 'int' },
  { key: 'clicks', label: 'Clicks', polarity: 'higher_is_better', format: 'int' },
  { key: 'sales30d', label: 'Sales', polarity: 'higher_is_better', format: 'currency' },
  { key: 'acos', label: 'ACoS', polarity: 'lower_is_better', format: 'percent' },
  { key: 'roas', label: 'ROAS', polarity: 'higher_is_better', format: 'ratio' },
  { key: 'asp', label: 'ASP', polarity: 'higher_is_better', format: 'currency' },
];

function num(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

export function deriveMetrics(raw: RawMetricTotals): DerivedMetricTotals {
  const { impressions, clicks, cost, purchases30d, sales30d } = raw;
  return {
    impressions,
    clicks,
    cost,
    purchases30d,
    sales30d,
    ctr: impressions > 0 ? (clicks / impressions) * 100 : 0,
    cpc: clicks > 0 ? cost / clicks : 0,
    cr: clicks > 0 ? (purchases30d / clicks) * 100 : 0,
    acos: sales30d > 0 ? (cost / sales30d) * 100 : 0,
    roas: cost > 0 ? sales30d / cost : 0,
    asp: purchases30d > 0 ? sales30d / purchases30d : 0,
    cpo: purchases30d > 0 ? cost / purchases30d : 0,
  };
}

/** Sum SP/SB/SD ad-product maps into account-level derived totals (for workspace KPI strip). */
export function totalsFromAdProductMaps(maps: WorkspaceMetricsMaps): DerivedMetricTotals {
  return totalsFromMetricRows(
    AD_PRODUCT_METRIC_ROWS.map(
      ({ reportId }) => maps.byAdProduct.get(reportId) ?? EMPTY_RAW_METRICS
    )
  );
}

/**
 * TACoS = ad spend / total retail sales × 100.
 * Returns 0 when total sales is missing or zero.
 */
export function deriveTacos(adSpend: number, totalSales: number | null | undefined): number {
  if (totalSales == null || totalSales <= 0) return 0;
  return (adSpend / totalSales) * 100;
}

export type ResolvedRetailKpiMetrics = {
  totalSales: number;
  organicSales: number;
  /** Ad sales shown as Paid (full attributed ads). */
  paidSales: number;
  /** Uncapped 30-day attributed ad sales. */
  attributedAdSales: number;
  adsExceedRetail: boolean;
  totalOrders: number;
  organicOrders: number;
  paidOrders: number;
  attributedAdOrders: number;
  adsOrdersExceedRetail: boolean;
  tacos: number;
};

/**
 * Resolve account retail KPIs for the workspace strip and snapshot gauges.
 * Total is max(vendor retail, ad sales) so missing vendor days cannot pull Total below ads.
 * Paid is full attributed ad sales; organic is the remainder of Total.
 * Until a Vendor Central account is linked, organic defaults to 0 and total = ad sales.
 */
export function resolveRetailKpiMetrics(
  ad: Pick<DerivedMetricTotals, 'cost' | 'sales30d' | 'purchases30d'>,
  retail: WorkspaceRetailKpiMetrics = EMPTY_RETAIL_KPI_METRICS
): ResolvedRetailKpiMetrics {
  const attributedAdSales = ad.sales30d;
  const attributedAdOrders = ad.purchases30d;
  const vendorSales = retail.totalSales != null ? Number(retail.totalSales) : null;
  const vendorOrders = retail.totalOrders != null ? Number(retail.totalOrders) : null;
  const totalSales =
    vendorSales != null
      ? Math.max(vendorSales, attributedAdSales)
      : (retail.organicSales ?? 0) + attributedAdSales;
  const totalOrders =
    vendorOrders != null
      ? Math.max(vendorOrders, attributedAdOrders)
      : (retail.organicOrders ?? 0) + attributedAdOrders;
  const paidSales = Math.max(0, attributedAdSales);
  const organicSales = Math.max(0, totalSales - paidSales);
  const paidOrders = Math.max(0, attributedAdOrders);
  const organicOrders = Math.max(0, totalOrders - paidOrders);
  const tacos = retail.tacos ?? deriveTacos(ad.cost, totalSales);
  return {
    totalSales,
    organicSales,
    paidSales,
    attributedAdSales,
    adsExceedRetail: vendorSales != null && attributedAdSales > vendorSales,
    totalOrders,
    organicOrders,
    paidOrders,
    attributedAdOrders,
    adsOrdersExceedRetail: vendorOrders != null && attributedAdOrders > vendorOrders,
    tacos,
  };
}

/** Re-derive all ratio metrics from raw totals (fills cr / acos / asp when API omits them). */
export function ensureDerivedMetrics(
  metrics: Partial<RawMetricTotals> | null | undefined
): DerivedMetricTotals {
  if (!metrics) return EMPTY_DERIVED_METRICS;
  return deriveMetrics({
    impressions: typeof metrics.impressions === 'number' ? metrics.impressions : 0,
    clicks: typeof metrics.clicks === 'number' ? metrics.clicks : 0,
    cost: typeof metrics.cost === 'number' ? metrics.cost : 0,
    purchases30d: typeof metrics.purchases30d === 'number' ? metrics.purchases30d : 0,
    sales30d: typeof metrics.sales30d === 'number' ? metrics.sales30d : 0,
  });
}

export function sumRawMetrics(a: RawMetricTotals, b: RawMetricTotals): RawMetricTotals {
  return {
    impressions: a.impressions + b.impressions,
    clicks: a.clicks + b.clicks,
    cost: a.cost + b.cost,
    purchases30d: a.purchases30d + b.purchases30d,
    sales30d: a.sales30d + b.sales30d,
  };
}

/** Sum raw metric fields across rows, then re-derive ratios (do not average ratios). */
export function totalsFromMetricRows(
  rows: Array<Pick<RawMetricTotals, keyof RawMetricTotals>>
): DerivedMetricTotals {
  let acc = { ...EMPTY_RAW_METRICS };
  for (const row of rows) {
    acc = sumRawMetrics(acc, {
      impressions: row.impressions ?? 0,
      clicks: row.clicks ?? 0,
      cost: row.cost ?? 0,
      purchases30d: row.purchases30d ?? 0,
      sales30d: row.sales30d ?? 0,
    });
  }
  return deriveMetrics(acc);
}

export function campaignLookupKey(adProduct: string, campaignId: string): string {
  return `${adProduct}::${campaignId}`;
}

export function campaignLookupKeyFromNode(campaign: CampaignNode): string {
  return campaignLookupKey(campaignAdProductToReportId(campaign.adProduct), campaign.campaignId);
}

export function rowToRaw(row: ReportRow): RawMetricTotals {
  return {
    impressions: num(row.impressions),
    clicks: num(row.clicks),
    cost: num(row.cost),
    purchases30d: num(row.purchases30d),
    sales30d: num(row.sales30d),
  };
}

export function accumulateInto(
  map: Map<string, RawMetricTotals>,
  key: string,
  addition: RawMetricTotals
): void {
  const existing = map.get(key) ?? { ...EMPTY_RAW_METRICS };
  map.set(key, sumRawMetrics(existing, addition));
}

export function finalizeRawMap(map: Map<string, RawMetricTotals>): Map<string, DerivedMetricTotals> {
  const out = new Map<string, DerivedMetricTotals>();
  for (const [key, raw] of map) {
    out.set(key, deriveMetrics(raw));
  }
  return out;
}

export function rowInCampaignScope(
  adProduct: string,
  campaignId: string,
  scopedCampaignLookupKeys: Set<string> | null
): boolean {
  if (scopedCampaignLookupKeys === null) return true;
  if (scopedCampaignLookupKeys.size === 0) return false;
  return scopedCampaignLookupKeys.has(campaignLookupKey(adProduct, campaignId));
}

export function buildScopedCampaignLookupKeys(campaigns: CampaignNode[]): Set<string> | null {
  if (campaigns.length === 0) return new Set();
  const keys = new Set<string>();
  for (const campaign of campaigns) {
    keys.add(campaignLookupKeyFromNode(campaign));
  }
  return keys;
}

/** Resolved campaign IDs from Workspace lookup-key scope (`adProduct::campaignId`). */
export type LookupScopeResolution =
  | { status: 'not_ready' }
  | { status: 'unscoped' }
  | { status: 'constrained'; ids: string[] };

export function campaignIdFromLookupKey(key: string): string {
  const sep = key.indexOf('::');
  return sep >= 0 ? key.slice(sep + 2) : key;
}

export function resolveCampaignIdsFromLookupScope(
  scopedCampaignLookupKeys: Set<string> | null | undefined
): LookupScopeResolution {
  if (scopedCampaignLookupKeys === undefined) return { status: 'not_ready' };
  if (scopedCampaignLookupKeys === null) return { status: 'unscoped' };
  const ids = new Set<string>();
  for (const key of scopedCampaignLookupKeys) {
    const id = campaignIdFromLookupKey(key);
    if (id) ids.add(id);
  }
  return { status: 'constrained', ids: [...ids] };
}

/** False when graph/scope is not ready or the cross-filter matches zero campaigns. */
export function lookupScopeShouldFetch(scope: LookupScopeResolution): boolean {
  if (scope.status === 'not_ready') return false;
  if (scope.status === 'constrained' && scope.ids.length === 0) return false;
  return true;
}

export function lookupScopeCampaignIdsParam(
  scope: LookupScopeResolution
): string[] | undefined {
  return scope.status === 'constrained' ? scope.ids : undefined;
}

export function lookupScopeCampaignIdSet(
  scopedCampaignLookupKeys: Set<string> | null | undefined
): Set<string> | null {
  const resolved = resolveCampaignIdsFromLookupScope(scopedCampaignLookupKeys);
  if (resolved.status !== 'constrained') return null;
  return new Set(resolved.ids);
}

export function joinCommaIds(ids: string[] | undefined): string | undefined {
  if (!ids || ids.length === 0) return undefined;
  return ids.join(',');
}

export function aggregateAdProductMetrics(
  rows: ReportRow[],
  scopedCampaignLookupKeys: Set<string> | null
): Map<string, DerivedMetricTotals> {
  const rawByProduct = new Map<string, RawMetricTotals>();
  for (const id of REPORT_AD_PRODUCT_IDS) {
    rawByProduct.set(id, { ...EMPTY_RAW_METRICS });
  }

  for (const row of rows) {
    const campaignId = row.campaignId ? String(row.campaignId) : '';
    const adProduct = row.adProduct ? String(row.adProduct) : 'sponsored_products';
    if (!campaignId || !rowInCampaignScope(adProduct, campaignId, scopedCampaignLookupKeys)) continue;
    const raw = rowToRaw(row);
    const bucket = rawByProduct.get(adProduct) ?? { ...EMPTY_RAW_METRICS };
    rawByProduct.set(adProduct, sumRawMetrics(bucket, raw));
  }

  return finalizeRawMap(rawByProduct);
}

export function buildWorkspaceMetricsMaps(
  campaignRows: ReportRow[],
  keywordRows: ReportRow[],
  targetRows: ReportRow[],
  graph: EntityGraph,
  scopedCampaignLookupKeys: Set<string> | null
): WorkspaceMetricsMaps {
  const rawByCampaign = new Map<string, RawMetricTotals>();
  const rawByAdGroup = new Map<string, RawMetricTotals>();

  for (const row of campaignRows) {
    const campaignId = row.campaignId ? String(row.campaignId) : '';
    const adProduct = row.adProduct ? String(row.adProduct) : 'sponsored_products';
    if (!campaignId || !rowInCampaignScope(adProduct, campaignId, scopedCampaignLookupKeys)) continue;
    accumulateInto(rawByCampaign, campaignLookupKey(adProduct, campaignId), rowToRaw(row));
  }

  for (const row of keywordRows) {
    const campaignId = row.campaignId ? String(row.campaignId) : '';
    const adProduct = row.adProduct ? String(row.adProduct) : 'sponsored_products';
    if (!campaignId || !rowInCampaignScope(adProduct, campaignId, scopedCampaignLookupKeys)) continue;
    const raw = rowToRaw(row);
    const adGroupId = row.adGroupId ? String(row.adGroupId) : '';
    if (adGroupId) {
      accumulateInto(rawByAdGroup, adGroupId, raw);
    }
  }

  for (const row of targetRows) {
    const campaignId = row.campaignId ? String(row.campaignId) : '';
    const adProduct = row.adProduct ? String(row.adProduct) : 'sponsored_display';
    if (!campaignId || !rowInCampaignScope(adProduct, campaignId, scopedCampaignLookupKeys)) continue;
    const raw = rowToRaw(row);
    const adGroupId = row.adGroupId ? String(row.adGroupId) : '';
    if (adGroupId) {
      accumulateInto(rawByAdGroup, adGroupId, raw);
    }
  }

  const byCampaignKey = finalizeRawMap(rawByCampaign);
  const byAdGroupId = finalizeRawMap(rawByAdGroup);
  const byAdProduct = aggregateAdProductMetrics(campaignRows, scopedCampaignLookupKeys);

  const rawByPortfolio = new Map<string, RawMetricTotals>();
  for (const campaign of graph.campaigns) {
    const lookup = campaignLookupKeyFromNode(campaign);
    if (!rowInCampaignScope(
      campaignAdProductToReportId(campaign.adProduct),
      campaign.campaignId,
      scopedCampaignLookupKeys
    )) {
      continue;
    }
    const metrics = byCampaignKey.get(lookup);
    if (!metrics) continue;
    const portfolioId = portfolioIdForCampaign(campaign);
    accumulateInto(rawByPortfolio, portfolioId, metrics);
  }

  const byPortfolioId = finalizeRawMap(rawByPortfolio);

  return { byAdProduct, byCampaignKey, byPortfolioId, byAdGroupId };
}

export function metricsForCampaign(
  campaign: CampaignNode,
  maps: WorkspaceMetricsMaps
): DerivedMetricTotals {
  return maps.byCampaignKey.get(campaignLookupKeyFromNode(campaign)) ?? EMPTY_DERIVED_METRICS;
}

export function metricsForPortfolio(
  portfolioId: string,
  maps: WorkspaceMetricsMaps
): DerivedMetricTotals {
  return maps.byPortfolioId.get(portfolioId) ?? EMPTY_DERIVED_METRICS;
}

export function metricsForAdGroup(
  adGroupId: string,
  maps: WorkspaceMetricsMaps
): DerivedMetricTotals {
  return maps.byAdGroupId.get(adGroupId) ?? EMPTY_DERIVED_METRICS;
}

export function metricsForKeywordGroup(
  row: UnifiedKeywordRow,
  keywordRowsByLookup: Map<string, ReportRow>
): DerivedMetricTotals {
  let raw = { ...EMPTY_RAW_METRICS };
  let found = false;

  for (const source of row.sources) {
    const keywordId = source.keyword_id != null ? String(source.keyword_id).trim() : '';
    if (!keywordId) continue;
    const lookup = keywordReportLookupKey(source.adProduct as KeywordAdProduct, keywordId);
    const reportRow = keywordRowsByLookup.get(lookup);
    if (!reportRow) continue;
    found = true;
    raw = sumRawMetrics(raw, rowToRaw(reportRow));
  }

  if (!found) return EMPTY_DERIVED_METRICS;
  return deriveMetrics(raw);
}

export function buildKeywordRowLookup(rows: ReportRow[]): Map<string, ReportRow> {
  const map = new Map<string, ReportRow>();
  for (const row of rows) {
    const keywordId = row.keywordId ? String(row.keywordId) : '';
    const adProduct = row.adProduct ? String(row.adProduct) : 'sponsored_products';
    if (keywordId) {
      map.set(keywordReportLookupKey(adProduct, keywordId), row);
    }
    const naturalKey = keywordNaturalLookupKey(row.campaignId, row.keywordText, row.matchType);
    if (naturalKey) {
      map.set(naturalKey, row);
    }
  }
  return map;
}

export function buildTargetRowLookup(rows: ReportRow[]): Map<string, ReportRow> {
  const map = new Map<string, ReportRow>();
  for (const row of rows) {
    const targetId = row.targetId ? String(row.targetId) : '';
    if (!targetId) continue;
    const adProduct = row.adProduct ? String(row.adProduct) : 'sponsored_products';
    map.set(targetReportLookupKey(adProduct, targetId), row);
    map.set(targetId, row);
  }
  return map;
}

export function filterKeywordRowsInScope(
  rows: ReportRow[],
  scopedCampaignLookupKeys: Set<string> | null
): ReportRow[] {
  return rows.filter((row) => {
    const campaignId = row.campaignId ? String(row.campaignId) : '';
    const adProduct = row.adProduct ? String(row.adProduct) : 'sponsored_products';
    return campaignId && rowInCampaignScope(adProduct, campaignId, scopedCampaignLookupKeys);
  });
}